<template>
  <div>
    <top-header v-bind:title="$t('priceTagCreation')">
      <header-button icon="mdi-import"
                     right
                     slot="right-button"
                     v-bind:label="$t('import')"
                     v-on:click="showImportCsvPicker"
                     v-bind:disabled="loading || currentType.manual" />
      <v-tabs background-color="transparent"
              dark
              slot="tabs"
              v-model="tab">
        <v-tab v-for="t in articleTypes"
               v-bind:key="t.key"
               v-bind:href="`#${t.key}`">{{ $t(t.key) }}</v-tab>
      </v-tabs>
    </top-header>
    <v-progress-linear color="accent"
                       indeterminate
                       v-if="loading" />
    <v-container>
      <div class="d-flex justify-end mb-4">
        <v-btn-toggle v-model="size"
                      dense
                      mandatory>
          <v-btn value="klein">{{ $t('sizeKlein') }}</v-btn>
          <v-btn value="a4">{{ $t('sizeA4') }}</v-btn>
        </v-btn-toggle>
      </div>
      <manual-price-tag-table-card v-if="currentType.manual"
                                   v-bind:products="manualProducts"
                                   v-bind:loading="loading"
                                   v-on:print="onPrint" />
      <price-tag-product-table-card v-else-if="!currentType.sale"
                                    ref="productTable"
                                    v-bind:products="doProducts"
                                    v-bind:loading="loading"
                                    v-bind:loading-percent="loadingPercent"
                                    v-bind:print-style="currentType.printStyle"
                                    v-on:print="onPrint"
                                    v-on:change-count="changeCount" />
      <sale-product-table-card v-else
                               ref="saleProductTableCard"
                               v-bind:sale-products="saleProducts"
                               v-bind:loading="loading"
                               v-on:print="onPrint" />
    </v-container>
    <input accept="text/csv"
           class="hidden"
           ref="fileInput"
           type="file"
           v-on:change="fileInputChange">
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'

  import Papa from 'papaparse'

  import TopHeader from '@/components/common/TopHeader.vue'
  import HeaderButton from '@/components/common/HeaderButton.vue'
  import PriceTagProductTableCard from '@/components/price-tag/PriceTagProductTableCard.vue'
  import SaleProductTableCard from '@/components/price-tag/SaleProductTableCard.vue'
  import ManualPriceTagTableCard from '@/components/price-tag/ManualPriceTagTableCard.vue'

  // Each article type is available in two sizes, chosen via the Klein/A4 toggle:
  //   klein -> backend-generated PDF (printStyle sent to /price-tag/pdf)
  //   a4    -> frontend HTML print (variant passed to the price-tag-print view)
  const ARTICLE_TYPES = [
    { key: 'gebinde',     sale: false, printStyle: 'bundles',     variant: 'kasten' },
    { key: 'xpack',       sale: false, printStyle: 'packs',       variant: 'xpack' },
    { key: 'einzel',      sale: false, printStyle: 'single',      variant: 'single' },
    { key: 'wss',         sale: false, printStyle: 'alcohol',     variant: 'wss' },
    { key: 'food',        sale: false, printStyle: 'food',        variant: 'food' },
    { key: 'sonstiges',   sale: false, printStyle: 'sweets',      variant: 'sonstiges' },
    { key: 'aktionen',    sale: true,  printStyle: 'smallSales',  variant: 'sale' },
    { key: 'aktionenWss', sale: true,  printStyle: 'saleAlcohol', variant: 'saleWss' },
    // manual -> Schilder werden ohne Artikelstamm aus manuellen Eingaben gebaut (nur im Speicher, nicht persistiert)
    { key: 'manuell',     sale: false, printStyle: 'manual',      variant: 'manual', manual: true }
  ]

  export default {
    components: {
      TopHeader,
      HeaderButton,
      PriceTagProductTableCard,
      SaleProductTableCard,
      ManualPriceTagTableCard
    },
    computed: {
      articleTypes() {
        return ARTICLE_TYPES
      },
      currentType() {
        return this.articleTypes.find(t => t.key === this.tab) || this.articleTypes[0]
      },
      saleProducts() {
        return this.liveSalePrices.map(s => ({
          ...s,
          key: `${s.productId}-${s.unit}-${s.start}-${s.end}`,
          count: 0,
          sale: true,
          saleText: ''
        }))
      },
      ...mapGetters({
        liveProducts: 'common/liveProducts',
        liveSalePrices: 'common/liveSalePrices',
        siteId: 'common/currentSiteId'
      })
    },
    async created() {
      this.loading = true
      await this.loadLiveProducts()
      await this.loadLiveSalePrices({ siteId: this.siteId })
      this.loading = false
    },
    data: () => ({
      doProducts: [],
      manualProducts: [],
      loading: false,
      tab: 'gebinde',
      size: 'a4',
      loadingPercent: 0
    }),
    methods: {
      onPrint() {
        const type = this.currentType

        if (type.manual) {
          return this.size === 'a4' ? this.printManualA4(type.variant) : this.printManualSmall(type.printStyle)
        }

        if (this.size === 'a4') {
          return type.sale ? this.printSalesA4(type.variant) : this.printA4(type.variant)
        }

        return type.sale ? this.printSmallSales(type.printStyle) : this.printSmall()
      },
      changeCount({ index, value }) {
        this.doProducts[index].count = parseInt(value)
      },
      readCsv(file) {
        return new Promise((resolve, reject) => {
          Papa.parse(file, {
            complete: results => {
              resolve(results.data)
            },
            error: err => {
              reject(err)
            },
            header: true,
            skipEmptyLines: true,
            transform: (value, field) => {
              return value
            }
          })
        })
      },
      async fileInputChange(ev) {
        if (ev == null) {
          return
        }

        const file = ev.srcElement.files[0]

        if (file == null) {
          return
        }

        try {
          this.loading = true

          const reader = new FileReader()

          reader.onloadend = async () => {
            this.loading = false

            await this.importCsv(reader.result)

          }

          reader.readAsText(file)
        } catch (e) {
          this.loading = false
        }
      },
      async printSmall()
      {
        this.loading = true

        const printStyle = this.currentType.printStyle

        const products = this.doProducts.map(p => ({
          siteId: this.siteId,
          productId: p.productId,
          count: p.count,
          printStyle
        }))


        const blob = await this.createPriceTagPdf({ productPriceTags: products })

        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')

        a.href = url
        a.download = `Preisschilder.pdf`

        document.body.appendChild(a);

        a.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window
          })
        )

        a.remove()

        this.loading = false
      },
      async printSmallSales(printStyle)
      {
        this.loading = true

        const products = this.saleProducts.filter(p => p.count > 0).map(p => ({
          siteId: p.siteId,
          productId: p.productId,
          count: p.count,
          printStyle,
          salePrice: p.price,
          saleUnit: p.unit,
          saleStorageFactor: p.storageFactor,
          saleCapacity: p.capacity,
          date: p.end
        }))


        const blob = await this.createPriceTagPdf({ productPriceTags: products })

        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')

        a.href = url
        a.download = `Preisschilder.pdf`

        document.body.appendChild(a);

        a.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window
          })
        )

        a.remove()

        this.loading = false
      },
      async printA4(variant) {
        await this.priceTagSetProducts({ products: this.doProducts })

        this.$router.push({ name: 'price-tag-print', query: { variant } })
      },
      async printSalesA4(variant) {
        this.loading = true

        const filteredSaleProducts = this.saleProducts.filter(p => p.count > 0)

        let doProducts = []

        for (let saleProduct of filteredSaleProducts) {
          let product = this.liveProducts.find(p => p.productId === saleProduct.productId)


          if (!product) {
            continue
          }

          await this.loadLivePrices({ siteId: this.siteId, productId: saleProduct.productId })

          product.count = saleProduct.count
          product.sale = true
          product.saleText = saleProduct.saleText
          product.salePrice = saleProduct.price
          product.saleUnit = saleProduct.unit
          product.saleQuantity = saleProduct.storageFactor
          product.deposit = saleProduct.deposit
          product.date = saleProduct.start && saleProduct.end ? `${saleProduct.start} - ${saleProduct.end}` : ''

          doProducts.push(product)
        }

        await this.priceTagSetProducts({ products: doProducts })

        this.loading = false

        this.$router.push({ name: 'price-tag-print', query: { variant } })
      },
      manualProductsToPrint() {
        return this.manualProducts.filter(p => p.count > 0)
      },
      async printManualSmall(printStyle) {
        this.loading = true

        try {
          const products = this.manualProductsToPrint().map(p => ({
            siteId: this.siteId,
            productId: p.productId || '',
            count: p.count,
            printStyle,
            publicTitle: p.publicTitle,
            label: p.label || '',
            quantity: p.quantity > 0 ? p.quantity : null,
            depositBaseUnit: p.depositBaseUnit,
            price: p.price
          }))

          const blob = await this.createPriceTagPdf({ productPriceTags: products })

          this.downloadPdf(blob, 'Preisschilder.pdf')
        } finally {
          this.loading = false
        }
      },
      async printManualA4(variant) {
        // Preisstruktur wie bei den Live-Preisen nachbilden, damit die Druckansicht keine Preise nachladen muss.
        const products = this.manualProductsToPrint().map(p => ({
          ...p,
          layout: 'manual',
          basePrice: {
            price: p.price,
            depositBaseUnit: p.depositBaseUnit,
            deposit: p.quantity > 0 ? p.depositBaseUnit * p.quantity : p.depositBaseUnit
          },
          secondaryPrice: {}
        }))

        await this.priceTagSetProducts({ products })

        this.$router.push({ name: 'price-tag-print', query: { variant } })
      },
      downloadPdf(blob, fileName) {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')

        a.href = url
        a.download = fileName

        document.body.appendChild(a)

        a.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window
          })
        )

        a.remove()
      },
      showImportCsvPicker() {
        this.$refs.fileInput.click()
      },
      async importCsv(csv) {
        this.loading = true
        const result = await this.readCsv(csv)

        const productIds = result.map(r => r?.Artikelnummer).filter(id => !!id)

        await this.loadLivePricesMultiple({ siteId: this.siteId, productIds })

        const table = this.$refs.productTable

        if (!table) {
          this.loading = false
          return
        }

        // A4 is frontend-rendered via the variant, so products carry no printStyle there.
        const printStyle = this.size === 'klein' ? this.currentType.printStyle : null

        for (let element of result) {
          await table.addProductByIdPreloaded(element?.Artikelnummer, parseInt(element?.Anzahl, 10), printStyle)
        }

        this.$refs.fileInput.value = ''
        this.loading = false
      },
      ...mapActions({
        loadLiveProducts: 'common/loadLiveProducts',
        priceTagSetProducts: 'priceTag/setProducts',
        loadLiveSalePrices: 'common/loadLiveSalePrices',
        loadLivePrices: 'common/loadLivePrices',
        createPriceTagPdf: 'priceTag/createPriceTagPdf',
        loadLivePricesMultiple: 'common/loadLivePricesMultiple'
      })
    },
    name: 'PriceTag'
  }
</script>

<i18n>
  {
    "de": {
      "priceTagCreation": "Preisschilderstellung",
      "import": "Importieren",
      "sizeKlein": "Klein",
      "sizeA4": "A4",
      "gebinde": "Gebinde",
      "xpack": "X-Pack",
      "einzel": "Einzel",
      "wss": "WSS",
      "food": "Food",
      "sonstiges": "Sonstiges",
      "aktionen": "Aktionen",
      "aktionenWss": "Aktionen WSS",
      "manuell": "Manuell"
    },
    "en": {
      "priceTagCreation": "Price tag creation",
      "import": "Import",
      "sizeKlein": "Small",
      "sizeA4": "A4",
      "gebinde": "Bundles",
      "xpack": "X-Pack",
      "einzel": "Single",
      "wss": "WSS",
      "food": "Food",
      "sonstiges": "Other",
      "aktionen": "Sale",
      "aktionenWss": "Sale WSS",
      "manuell": "Manual"
    }
  }
</i18n>
